/**
 * 动态适配下拉框
 */

import { Select, SelectValue, SelectOptionValue } from '@tencent/udesign/dist';
import {
  getAllowClearConfig,
  getDynamicSelectedValue,
  getDynamicSelectOption,
  getPlaceholderConfig,
  getSelectTypeConfig,
  getTableBodyLinkageValue,
  isSelectComponent,
  getCheckItem,
  searchOptions,
} from '../../../libs';
import {
  FieldType,
  IFieldValueExtensionSelect,
  IFieldTypeOptBySelect,
  IPropsSelect,
} from '../../../model';
import { ForwardSelectedLabel } from './render-selected-label';
import { useRef, useState, useEffect } from 'react';
// select 下拉框

export const DynamicSelect = <T,>(props: IPropsSelect<T>) => {
  const { isFilterModeView, fieldConfig, selectedValue } = props;
  const filterOptExtension = isSelectComponent(fieldConfig.filterOptExtension);
  const isMultiple = fieldConfig.filter_type === FieldType.select_multiple;
  const selectAllLabel =
    (filterOptExtension as IFieldTypeOptBySelect).selectAllLabel || '全选';
  const selectedLabelRef = useRef<any | null>(null);
  const [openSelect, setOpenSelect] = useState<boolean>(false);
  const [options, setOptions] = useState<Array<IFieldValueExtensionSelect>>([]);
  const [newOptions, setNewOptions] = useState<
    Array<IFieldValueExtensionSelect>
  >([]);
  const [selectAllValue, setSelectAllValue] = useState<
    ReadonlyArray<SelectOptionValue>
  >([]);

  useEffect(() => {
    if (
      isFilterModeView &&
      filterOptExtension &&
      (fieldConfig.filter_type === FieldType.select_multiple ||
        fieldConfig.filter_type === FieldType.select)
    ) {
      const isSelectAll = isMultiple && filterOptExtension.selectAll;
      const options = getDynamicSelectOption(
        selectedValue.value,
        fieldConfig?.fieldOptExtension?.data || props.options,
        isSelectAll,
        selectAllLabel
      );
      setOptions(options);
      setNewOptions(
        searchOptions(selectedLabelRef?.current?.getKeyword() || '', options)
      );
      // 获取全选列表
      if (isSelectAll && options.length > 0) {
        setSelectAllValue(options.map((item) => item.value));
      }
    }
  }, [
    isFilterModeView,
    filterOptExtension,
    fieldConfig?.fieldOptExtension?.data,
    props.options,
  ]);

  // Filters
  if (isFilterModeView) {
    const filterOptExtension = isSelectComponent(
      fieldConfig.filterOptExtension
    );
    if (!filterOptExtension) {
      return null;
    }

    const type = getSelectTypeConfig(filterOptExtension);
    const isGroup = filterOptExtension?.kind === FieldType.select_group;
    const isSelectAll = isMultiple && filterOptExtension.selectAll; // 当 筛选框 为多选 而且 使用全选功能时
    const placeholder = getPlaceholderConfig(fieldConfig);
    const newValue = getDynamicSelectedValue(selectedValue, isMultiple);
    const getCurrentValue = (value: SelectValue) => {
      if (Array.isArray(value) && Array.isArray(newValue)) {
        let currentSelect = value.slice();
        if (!newValue.length) return currentSelect;
        const newOptValues = newOptions.map((v) => v.value);
        for (const it of newValue) {
          if (!value.includes(it as unknown as never)) {
            if (!newOptValues.includes(it)) {
              currentSelect = [...currentSelect, it];
            }
          }
        }
        return currentSelect;
      }
      return [];
    };

    const onChanged = (value: SelectValue) => {
      const clearSearchKeywords =
        typeof filterOptExtension?.clearSearchKeywords === 'boolean'
          ? !!filterOptExtension?.clearSearchKeywords
          : true;

      // 当使用 全选功能 时 在选中项的回调中清空子组件的input参数
      if (selectedLabelRef?.current && isSelectAll && clearSearchKeywords) {
        selectedLabelRef.current.emptyInput();
      }

      // 确定现在选择的所有值
      const currentSelect = isMultiple ? getCurrentValue(value) : value;

      // 设置新的下拉选项
      if (clearSearchKeywords) {
        const list = searchOptions('', options);
        setNewOptions(list);
      }

      // 当启用全选功能时
      if (isSelectAll && Array.isArray(currentSelect)) {
        const checkItem = getCheckItem(currentSelect, newValue, selectAllValue);
        return props.onChanged(fieldConfig.key, checkItem);
      }

      props.onChanged(fieldConfig.key, currentSelect);
    };

    const onClickClear = () => {
      onChanged([]);
    };

    const onFocus = () => {
      // 当使用 全选功能 才 使用 手动聚焦input框
      if (selectedLabelRef?.current && isSelectAll) {
        selectedLabelRef.current.focusInput();
      }
    };

    // 根据子组件 input 值过滤筛选参数
    const selectValue = (keyWord: string) => {
      const list = searchOptions(keyWord, options);
      setNewOptions(list);
    };

    const renderLabel = () => {
      return (
        <ForwardSelectedLabel
          type={type}
          placeholder={placeholder}
          isMultiple={isMultiple}
          selectAllLabel={selectAllLabel}
          fieldConfig={fieldConfig}
          onClickClear={onClickClear}
          newValue={newValue}
          isSelectAll={isSelectAll}
          openSelect={openSelect}
          setOpenSelect={setOpenSelect}
          selectValue={selectValue}
          ref={selectedLabelRef}
        />
      );
    };
    return (
      <Select
        type={type}
        placeholder={placeholder}
        value={newValue}
        onChanged={onChanged}
        options={
          isGroup
            ? undefined
            : (newOptions as Array<IFieldValueExtensionSelect>)
        }
        mode={isMultiple ? 'multiple' : undefined}
        allowClear={getAllowClearConfig(filterOptExtension)}
        className={props.className}
        renderSelectedLabel={isSelectAll ? renderLabel : undefined}
        onFocus={onFocus}
        open={isSelectAll ? openSelect : undefined}
        {...filterOptExtension}
        dropDownProps={{
          container: () =>
            document.getElementsByClassName('fullscreen-enabled')[0] ||
            document.body,
        }}
      >
        {isGroup &&
          (
            fieldConfig?.filterOptExtension as IFieldTypeOptBySelect
          )?.groups?.map((group) => (
            <Select.OptionGroup label={group.label} groupKey={group.groupKey}>
              {group?.options?.map((option) => (
                <Select.Option value={option?.value} label={option?.label}>
                  {option?.label}
                </Select.Option>
              ))}
            </Select.OptionGroup>
          ))}
      </Select>
    );
  }

  const bodyValue = getTableBodyLinkageValue(selectedValue, fieldConfig);
  if (bodyValue) {
    return <span>{bodyValue}</span>;
  }
  return <span>{selectedValue.value}</span>;
};
